import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable, fmtDate } from "@/components/admin/DataTable";
import { errMsg, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";

const GROUPS = [["all", "All"], ["learners", "Learners"], ["instructors", "Instructors"], ["organisation", "Organisation users"], ["admins", "Administrators"]];

function InviteDialog({ open, onClose, roles }) {
  const api = useApi();
  const [f, setF] = useState({ email: "", role: "learner" });
  const send = async () => {
    try {
      await api.post("/admin/invitations", { ...f, redirect_url: `${window.location.origin}/sign-up` });
      toast.success("Invitation sent");
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Invite a user</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label htmlFor="inv-email">Email address</Label><Input id="inv-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email" /></div>
          <div className="space-y-1.5"><Label>Role</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <DialogFooter><Button onClick={send} className="bg-[#222d99] hover:bg-[#1a237e]" data-testid="invite-send">Send invitation</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const api = useApi();
  const { me } = useMe();
  const perms = me.user.permissions;
  const [params, setParams] = useSearchParams();
  const [group, setGroup] = useState("all");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState([]);
  const [roles, setRoles] = useState([]);
  const [bulk, setBulk] = useState(null);
  const [reason, setReason] = useState("");
  const [reload, setReload] = useState(0);
  useEffect(() => { api.get("/admin/roles").then((r) => setRoles(r.data.roles)).catch(() => {}); }, [api]);
  const names = Object.fromEntries(roles.map((r) => [r.key, r.name]));

  const runBulk = async () => {
    try {
      const { data } = await api.post("/admin/users/bulk", { ids: selected, action: bulk, reason });
      toast.success(`${data.done} updated${data.skipped.length ? `, ${data.skipped.length} skipped` : ""}`);
      setBulk(null); setReason(""); setSelected([]); setReload((n) => n + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    { key: "name", label: "Name", sortable: true, render: (u) => <Link to={`/admin/users/${u.user_id}`} className="font-semibold text-[#222d99] underline-offset-4 hover:underline" data-testid={`user-link-${u.user_id}`}>{u.name}</Link> },
    { key: "email", label: "Email", sortable: true },
    { key: "role", label: "Role", sortable: true, render: (u) => names[u.role] || u.role },
    { key: "status", label: "Status", render: (u) => <span className="flex flex-wrap gap-1"><Badge className={u.status === "suspended" ? "bg-rose-700" : "bg-emerald-700"}>{u.status || "active"}</Badge>{u.is_test && <Badge variant="outline">Test data</Badge>}</span> },
    { key: "created_at", label: "Joined", sortable: true, defaultSort: true, render: (u) => fmtDate(u.created_at) },
  ];

  return (
    <div>
      <PageHeader eyebrow="Admin Control Centre" title="Users">
        {perms.includes("users.invite") && <Button onClick={() => setParams({ invite: "1" })} className="rounded-full bg-[#222d99] hover:bg-[#1a237e]" data-testid="invite-open-btn">Invite user</Button>}
      </PageHeader>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Tabs value={group} onValueChange={setGroup}><TabsList className="h-auto flex-wrap justify-start">{GROUPS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`users-group-${v}`}>{l}</TabsTrigger>)}</TabsList></Tabs>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="lg:w-40" data-testid="users-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any status</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="suspended">Suspended</SelectItem></SelectContent></Select>
      </div>
      {perms.includes("users.suspend") && selected.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[#222d99]/30 bg-[#e8eaf6] p-3 text-sm" data-testid="bulk-bar">
          <span className="font-semibold">{selected.length} selected</span>
          <Button size="sm" variant="outline" onClick={() => setBulk("suspend")} data-testid="bulk-suspend">Suspend</Button>
          <Button size="sm" variant="outline" onClick={() => setBulk("reinstate")} data-testid="bulk-reinstate">Reinstate</Button>
        </div>
      )}
      <DataTable endpoint="/admin/users" params={{ group, status: status === "all" ? "" : status }} columns={columns} rowKey="user_id"
        csvName={`crowlearn-users-${group}`} selectable={perms.includes("users.suspend")} selected={selected} onSelect={setSelected} testid="users-table" reloadKey={reload} />
      <InviteDialog open={params.get("invite") === "1"} onClose={() => setParams({})} roles={roles.filter((r) => me.user.role === "super_admin" || !r.is_admin)} />
      <Dialog open={!!bulk} onOpenChange={(o) => !o && setBulk(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{bulk === "suspend" ? "Suspend" : "Reinstate"} {selected.length} user(s)?</DialogTitle></DialogHeader>
          {bulk === "suspend" && <div className="space-y-1.5"><Label htmlFor="bulk-reason">Reason (required)</Label><Textarea id="bulk-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="bulk-reason" /></div>}
          <DialogFooter><Button onClick={runBulk} disabled={bulk === "suspend" && reason.trim().length < 5} variant={bulk === "suspend" ? "destructive" : "default"} data-testid="bulk-confirm">Confirm</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
